/**
 * Account Settings Section
 * Logout, subscription info, and account management
 */
import React from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useSettingsStore } from '../../stores/settingsStore';

// Note: Since AccountSettings is inside SettingsModal, we can't directly change views.
// The subscription link in the Sidebar provides the navigation to SubscriptionPlans page.

export default function AccountSettings() {
  const { user, stats, logout, loadStats } = useAuthStore();
  const { closeSettings } = useSettingsStore();

  // Load stats on mount
  React.useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleLogout = async () => {
    if (confirm('Are you sure you want to log out?')) {
      await logout();
      closeSettings();
    }
  };

  const handleCancelSubscription = () => {
    window.open('https://app.chargekeep.com/p/15c2cf1b8afbd403/sign_in', '_blank');
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-dark-text mb-2">Account</h3>
        <p className="text-sm text-dark-text-muted">
          Manage your account settings and subscription.
        </p>
      </div>

      {/* Account Info */}
      <div className="bg-dark-bg/30 border border-dark-border/50 rounded-xl p-5 space-y-4">
        <div>
          <div className="text-xs text-dark-text-muted mb-1">Account Email</div>
          <div className="text-dark-text font-medium">{user?.email}</div>
        </div>

        <div>
          <div className="text-xs text-dark-text-muted mb-1">Account ID</div>
          <div className="text-dark-text font-mono text-sm">{user?.id}</div>
        </div>

        <div>
          <div className="text-xs text-dark-text-muted mb-1">Account Created</div>
          <div className="text-dark-text">
            {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }) : 'Unknown'}
          </div>
        </div>
      </div>

      {/* Subscription */}
      <div className="bg-dark-bg/30 border border-dark-border/50 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-semibold text-dark-text">Subscription Plan</h4>
            <p className="text-xs text-dark-text-muted mt-1">
              Your current subscription tier and limits
            </p>
          </div>
        </div>

        <div className="inline-block px-4 py-2 bg-accent-blue/10 border border-accent-blue/30 rounded-lg mb-4">
          <div className="text-accent-blue font-semibold capitalize">
            {user?.subscription_tier || 'Free'} Plan
          </div>
        </div>

        {/* Usage Stats */}
        {stats && (
          <div className="space-y-3 mt-4">
            <div className="bg-dark-bg/50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-dark-text-muted">Bridge Items</span>
                <span className="text-sm font-semibold text-dark-text">
                  {stats.total_clipboard_items} / {stats.clipboard_limit}
                </span>
              </div>
              <div className="w-full bg-dark-border/30 rounded-full h-2">
                <div
                  className="bg-accent-blue rounded-full h-2 transition-all"
                  style={{ width: `${Math.min((stats.total_clipboard_items / stats.clipboard_limit) * 100, 100)}%` }}
                />
              </div>
            </div>

            <div className="bg-dark-bg/50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-dark-text-muted">Conversations</span>
                <span className="text-sm font-semibold text-dark-text">
                  {stats.total_conversations} / {stats.conversation_limit}
                </span>
              </div>
              <div className="w-full bg-dark-border/30 rounded-full h-2">
                <div
                  className="bg-accent-blue rounded-full h-2 transition-all"
                  style={{ width: `${Math.min((stats.total_conversations / stats.conversation_limit) * 100, 100)}%` }}
                />
              </div>
            </div>

            <div className="bg-dark-bg/50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-dark-text-muted">AI Requests This Month</span>
                <span className="text-sm font-semibold text-dark-text">
                  {stats.total_ai_requests_this_month} / {stats.ai_request_limit === -1 ? '∞' : stats.ai_request_limit}
                </span>
              </div>
              {stats.ai_request_limit !== -1 && (
                <div className="w-full bg-dark-border/30 rounded-full h-2">
                  <div
                    className="bg-accent-blue rounded-full h-2 transition-all"
                    style={{ width: `${Math.min((stats.total_ai_requests_this_month / stats.ai_request_limit) * 100, 100)}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {user?.subscription_tier === 'basic' && (
          <div className="mt-4 p-4 bg-accent-blue/10 border border-accent-blue/30 rounded-xl">
            <p className="text-sm text-dark-text mb-2">
              Want more features? Upgrade your subscription plan.
            </p>
            <p className="text-xs text-dark-text-muted mb-3">
              To upgrade your account to a higher tier, please contact us and we'll get you set up.
            </p>
            <a
              href="mailto:corey@ministry.builders?subject=Subscription%20Upgrade%20Request"
              className="inline-block px-4 py-2 bg-gradient-to-r from-accent-blue to-accent-orange text-white text-sm font-semibold rounded-lg hover:shadow-lg hover:shadow-accent-blue/30 transition-all"
            >
              Contact Us to Upgrade
            </a>
          </div>
        )}

        {user?.subscription_tier === 'advanced' && (
          <div className="mt-4 p-4 bg-accent-blue/10 border border-accent-blue/30 rounded-xl">
            <p className="text-sm text-dark-text mb-2">
              Upgrade to Pro for unlimited access and advanced features.
            </p>
            <p className="text-xs text-dark-text-muted mb-3">
              To upgrade your account, please contact us and we'll get you set up.
            </p>
            <a
              href="mailto:corey@ministry.builders?subject=Subscription%20Upgrade%20Request%20-%20Pro"
              className="inline-block px-4 py-2 bg-gradient-to-r from-accent-blue to-accent-orange text-white text-sm font-semibold rounded-lg hover:shadow-lg hover:shadow-accent-blue/30 transition-all"
            >
              Contact Us to Upgrade
            </a>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="space-y-3 pt-4 border-t border-dark-border/50">
        <button
          onClick={handleLogout}
          className="w-full py-3 px-4 rounded-xl border border-dark-border/50 text-dark-text hover:bg-dark-bg/30 transition-colors flex items-center justify-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
          </svg>
          Log Out
        </button>

        <button
          onClick={handleCancelSubscription}
          className="w-full py-3 px-4 rounded-xl border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors"
        >
          Cancel Subscription
        </button>
      </div>

      {/* Support */}
      <div className="bg-dark-bg/30 border border-dark-border/50 rounded-xl p-5">
        <h4 className="text-sm font-semibold text-dark-text mb-2">Need Help?</h4>
        <p className="text-xs text-dark-text-muted mb-3">
          Contact our support team for assistance with your account.
        </p>
        <button
          onClick={() => window.open('mailto:support@paraclete.ai', '_blank')}
          className="text-accent-blue text-sm hover:underline"
        >
          Contact Support →
        </button>
      </div>
    </div>
  );
}
